import React from "react";
import Section from "../Section/Section";
import { useServices } from "../../services/Services";
import { Osdk } from "@osdk/client";
import { Event } from "@unruly-osdk/sdk";
import { Link } from "react-router-dom";
import Markdown from "react-markdown";
import foundryMarkdown from "../../../lib/FoundryMarkdown";
import AlertError from "../../AlertError/AlertError";
import EventIcon from "@mui/icons-material/Event";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  extractParagraphBeforeReferences,
  extractReferencedEvents,
  getDomainFromUrl,
  isInternalEvent,
} from "../../helper/helper";
import EntityList from "../EntityList/EntityList";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EventBroaderContextSkeleton from "./EventBroaderContextSkeleton";
import { EntityItem, EntityType } from "../../common-types/Entity";
import PillsGroup from "../PillsGroup/PillsGroup";
import { PillsVariant } from "../../common-types/Pill";
import styles from "../EventSummary/EventSummary.module.css";

interface EventBroaderContextProps {
  event?: Osdk.Instance<Event>;
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
  openLinksInNewTab?: boolean;
  context?: string;
  isContextLoading?: boolean;
  fetchContextFromAPI?: boolean;
  isContextError?: boolean;
  onEntityClick?: (id: string, type: EntityType) => void;
}

const EventBroaderContext: React.FC<EventBroaderContextProps> = ({
  event,
  onLinkClick,
  context,
  isContextLoading,
  fetchContextFromAPI,
  isContextError,
  onEntityClick,
  openLinksInNewTab = false,
}) => {
  const services = useServices();

  const {
    data: fetchedContext,
    isLoading,
    isError,
  } = services.Event.useGetEventBroaderContext(event, fetchContextFromAPI);

  const finalContext = context ?? fetchedContext;
  const isActuallyLoading = isContextLoading ?? isLoading;
  const isActualError = isContextError ?? isError;

  const referencedEvents = extractReferencedEvents(finalContext ?? "");
  const { data: referencedEventsData } = services.Event.useGetSourceEventsByIds(
    referencedEvents.map((refEvent) => refEvent.id),
  );

  const contextParagraph = extractParagraphBeforeReferences(finalContext ?? "");

  return (
    <Section>
      <div className={styles["secondary-header"]}>
        <h4>Event Summary</h4>
        <AutoAwesomeOutlinedIcon className={styles["icon"]} />
      </div>
      {isActuallyLoading && <EventBroaderContextSkeleton />}
      {isActualError && <AlertError />}
      {contextParagraph && (
        <>
          <Markdown
            remarkPlugins={[foundryMarkdown]}
            components={{
              a({ children, href }) {
                const isInternalLink = href && href.startsWith("/");
                return (
                  <Link
                    to={isInternalLink ? href : href || "#"}
                    onClick={isInternalLink ? onLinkClick : undefined}
                    {...(openLinksInNewTab &&
                      !isInternalLink && {
                        target: "_blank",
                        rel: "noopener noreferrer",
                      })}
                  >
                    {children}
                  </Link>
                );
              },
            }}
          >
            {contextParagraph}
          </Markdown>
        </>
      )}
      {referencedEventsData?.length !== 0 && (
        <>
          <h4>Referenced Events</h4>
          <EntityList
            grids={2}
            entities={
              referencedEventsData?.map((refEvent) => {
                const isInternalEventEvent = isInternalEvent(
                  refEvent.sourceType,
                );
                const sourceLabel = isInternalEventEvent
                  ? "Unruly / AFP"
                  : refEvent.newsSourceUrl
                    ? (getDomainFromUrl(refEvent.newsSourceUrl) ?? "No Source")
                    : "No Source";
                const sourceHref = isInternalEventEvent
                  ? undefined
                  : refEvent.newsSourceUrl || undefined;
                return {
                  name: refEvent.name,
                  id: refEvent.id,
                  type: "event",
                  description: (
                    <PillsGroup
                      pills={[
                        {
                          variant: PillsVariant.default,
                          value: refEvent.startDate ?? "",
                          label: "Date",
                          icon: EventIcon,
                        },
                        {
                          variant: PillsVariant.default,
                          value: sourceLabel,
                          href: sourceHref,
                          label: "Source",
                          icon: NewspaperOutlinedIcon,
                        },
                        ...(refEvent.id === event?.id
                          ? [
                              {
                                variant: PillsVariant.default,
                                tooltipText:
                                  "You are currently viewing this event",
                                value: "This event",
                                icon: VisibilityIcon,
                              },
                            ]
                          : []),
                      ]}
                    />
                  ),
                } as EntityItem;
              }) ?? []
            }
            onClick={onEntityClick}
          />
        </>
      )}
    </Section>
  );
};

export default EventBroaderContext;
